import { useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useFeatures } from "@/lib/features";
import { formatGBP } from "@/lib/api";
import { Banknote, Clock } from "lucide-react";

export const todayUK = () => new Date().toLocaleDateString("en-GB", { timeZone: "Europe/London" });
const METHODS = [["cash", "Cash"], ["card", "Card"], ["bank_transfer", "Bank transfer"]];

function Legacy({ form, set }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5"><Label className="text-sm font-medium text-navy">Payment Status</Label>
        <Select value={form.payment_status} onValueChange={(v) => set("payment_status", v)}>
          <SelectTrigger className="gm-input" data-testid="payment-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid / Supplier Credit</SelectItem>
            <SelectItem value="partial">Partial Payment</SelectItem>
          </SelectContent>
        </Select></div>
      {(form.payment_status === "paid" || form.payment_status === "partial") && (
        <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
          <SelectTrigger className="gm-input"><SelectValue placeholder="Select method" /></SelectTrigger>
          <SelectContent>{METHODS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
}

const Choice = ({ on, onClick, icon: Icon, title, sub, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`flex-1 min-h-[72px] rounded-xl border-2 p-3 text-left transition-colors ${on ? "border-navy bg-navy text-white" : "border-navy/15 bg-white text-navy"}`}>
    <Icon className="w-5 h-5 mb-1" /><div className="font-semibold text-sm">{title}</div><div className={`text-xs ${on ? "text-white/70" : "text-navy/50"}`}>{sub}</div>
  </button>
);

export function usePaymentDefaults(form, setForm, fresh) {
  const { phase2 } = useFeatures();
  useEffect(() => {
    if (phase2 && fresh) {
      setForm((p) => ({ ...p, payment_status: "paid", payment_method: p.payment_method || "cash", payment_date: p.payment_date || todayUK() }));
    }
  }, [phase2, fresh, setForm]);
}

export default function PaymentChoice({ form, set }) {
  const { phase2 } = useFeatures();
  useEffect(() => {
    if (phase2 && form.is_credit_note && form.payment_status === "paid") { set("payment_status", "unpaid"); }
  }, [phase2, form.is_credit_note, form.payment_status, set]);
  if (!phase2) return <Legacy form={form} set={set} />;
  if (form.is_credit_note) return <p className="text-xs text-navy/60" data-testid="credit-note-payment-note">Credit notes reduce what you owe the supplier; no payment is recorded.</p>;
  const paid = form.payment_status === "paid";
  const total = parseFloat(form.total_amount) || 0;
  const amt = form.paid_amount === "" || form.paid_amount === undefined ? total : parseFloat(form.paid_amount) || 0;
  return (
    <div className="space-y-4" data-testid="payment-choice">
      <div className="flex gap-3">
        <Choice on={paid} onClick={() => set("payment_status", "paid")} icon={Banknote} title="Paid in Full" sub="Paid now — recorded automatically" testid="paid-in-full-btn" />
        <Choice on={!paid} onClick={() => set("payment_status", "unpaid")} icon={Clock} title="Payment Pending" sub="We still owe the supplier" testid="payment-pending-btn" />
      </div>
      {paid ? (
        <div className="space-y-3 rounded-xl border border-navy/10 bg-white p-3" data-testid="paid-in-full-details">
          <div className="flex flex-wrap gap-2">
            {METHODS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => set("payment_method", v)} data-testid={`paid-method-${v}`}
                className={`min-h-[44px] px-4 rounded-full border text-sm transition-colors ${form.payment_method === v ? "bg-navy text-white border-navy" : "bg-white border-navy/15 text-navy"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs text-navy/60">Date paid</Label>
              <Input value={form.payment_date || ""} onChange={(e) => set("payment_date", e.target.value)} placeholder="DD/MM/YYYY" className="gm-input" data-testid="paid-date-input" /></div>
            <div className="space-y-1"><Label className="text-xs text-navy/60">Amount paid (£)</Label>
              <Input inputMode="decimal" value={form.paid_amount === undefined || form.paid_amount === "" ? (total || "") : form.paid_amount} onChange={(e) => set("paid_amount", e.target.value)} className="gm-input font-mono" data-testid="paid-amount-input" /></div>
          </div>
          {Math.abs(amt - total) > 0.005 && (
            <p className="text-xs text-amber-700" data-testid="paid-amount-mismatch">Paid in Full must equal the invoice total ({formatGBP(total)}). If only part was paid, choose Payment Pending and record the payment separately.</p>
          )}
        </div>
      ) : (
        <p className="text-xs text-navy/60" data-testid="payment-pending-note">The invoice will show as owed to the supplier until a payment is recorded.</p>
      )}
    </div>
  );
}

export const paymentPayload = (form, phase2) => (phase2
  ? { payment_status: form.payment_status, payment_method: form.payment_status === "paid" ? form.payment_method || null : null, payment_date: form.payment_status === "paid" ? form.payment_date || null : null }
  : { payment_status: form.payment_status, payment_method: form.payment_method || null, amount_paid: parseFloat(form.amount_paid) || 0 });

export const paidAmount = (form) => (form.paid_amount === undefined || form.paid_amount === "" ? parseFloat(form.total_amount) || 0 : parseFloat(form.paid_amount) || 0);
